// client/src/writer/hooks/useBookStructureManagement.ts
import { useCallback, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
// import { AppContext } from '../contexts/AppContext';
import { apiRequest } from '../../lib/queryClient'; // Use your existing helper
import { cacheManager } from '../../utils/cacheUtils';
import { useToast } from '../../hooks/use-toast';

import {
    AuthorDetails, FrontMatter, BackMatter, PublishingEssentials,
    AppendixItem, GlossaryTerm, BibliographyEntry, UpdateBackMatterListItemArgsTuple,
    BookStructureTextElementKey, GeneratedBookStructureTexts, Chapter
} from '../types';
import { CoreState } from './useCoreState';
import { ModalControls } from './useModalStates'; 
import { getActiveVersion } from '../utils/chapterUtils'; 
import { initialFrontMatter, initialBackMatter } from '../constants'; 

// Define more specific key types for text-based elements that are updated by the AI generation functions
type AiGeneratableFrontMatterTextKeys = Extract<BookStructureTextElementKey, keyof Pick<FrontMatter, 'dedication' | 'epigraph' | 'preface' | 'acknowledgmentsFront' | 'prologue' | 'introduction' | 'tableOfContents'>>;
type AiGeneratableBackMatterTextKeys = Extract<BookStructureTextElementKey, keyof Pick<BackMatter, 'epilogue' | 'afterword' | 'authorsNote' | 'acknowledgmentsBack' | 'readingGroupGuide' | 'teaserNextBook' | 'aboutTheAuthor' | 'index'>>;

const elementTypeToResponseKeyMap: Partial<Record<BookStructureTextElementKey, string>> = {
    dedication: 'dedicationText',
    prologue: 'prologueContent',
    preface: 'prefaceContent',
    introduction: 'introductionContent',
    acknowledgmentsFront: 'acknowledgmentsContent',
    epigraph: 'epigraphContent',
    tableOfContents: 'tableOfContents',
    epilogue: 'epilogueContent',
    afterword: 'afterwordContent',
    authorsNote: 'authorsNoteContent',
    acknowledgmentsBack: 'acknowledgmentsContent',
    aboutTheAuthor: 'aboutTheAuthorSection',
    readingGroupGuide: 'readingGroupGuideContent',
    teaserNextBook: 'teaserNextBookContent',
    index: 'indexContent',
};

export interface BookStructureManagement {
    handleAuthorDetailsChange: <K extends keyof AuthorDetails>(field: K, value: AuthorDetails[K]) => void;
    handleFrontMatterChange: <K extends keyof FrontMatter>(field: K, value: FrontMatter[K]) => void;
    handleBackMatterChange: <K extends keyof BackMatter>(field: K, value: BackMatter[K]) => void;
    handleAddBackMatterListItem: (listField: 'appendices' | 'glossary' | 'bibliography') => void;
    handleDeleteBackMatterListItem: (listField: 'appendices' | 'glossary' | 'bibliography', id: string) => void;
    handleUpdateBackMatterListItem: (...args: UpdateBackMatterListItemArgsTuple) => void;
    handlePublishingEssentialsChange: <K extends keyof PublishingEssentials>(field: K, value: PublishingEssentials[K]) => void;
    
    handleGenerateIndividualBookStructureTextElement: (elementType: BookStructureTextElementKey, isFrontMatter: boolean) => Promise<void>; 
    
    handleGenerateMultipleBookStructureElements: (elementsToGenerate: BookStructureTextElementKey[]) => Promise<void>;

    handleGenerateTitlePageImageAI: (prompt: string) => Promise<void>;
    handleGenerateBackCoverImageAI: (prompt: string) => Promise<void>;

    // These are now essentially covered by handleGenerateIndividualBookStructureTextElement
    // but kept for API compatibility if needed, though they could be marked @deprecated
    handleGenerateFrontMatterElement: (elementType: Extract<BookStructureTextElementKey, 'dedication' | 'prologue' | 'preface' | 'introduction' | 'acknowledgmentsFront' | 'epigraph' | 'tableOfContents'>) => Promise<void>;
    handleGenerateBackMatterElement: (elementType: Extract<BookStructureTextElementKey, 'epilogue' | 'afterword' | 'authorsNote' | 'acknowledgmentsBack' | 'aboutTheAuthor' | 'readingGroupGuide' | 'teaserNextBook' | 'index'>) => Promise<void>;
}

export const useBookStructureManagement = (
    coreState: CoreState,
    modalControls: ModalControls
): BookStructureManagement => {
    const { projectState, setProjectState, apiKey, setLoadingState, setError, getCoreProjectContextForAI } = coreState;
    // const appContext = useContext(AppContext);
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // if (!appContext) {
    //     throw new Error("useBookStructureManagement must be used within an AppContext.Provider");
    // }

    const aiMutation = useMutation({
        mutationFn: async ({ task, payload }: { task: string, payload: any }) => {
            const response = await apiRequest(`/api/llm/${task}`, "POST", payload);
            return response.json();
        },
        onSuccess: async (data, variables) => {
            const { task, payload } = variables;
            let successMessage = "AI action successful!";
            
            if (task === 'structure-element') {
                const { elementType, isFrontMatter } = payload;
                const responseKey = elementTypeToResponseKeyMap[elementType as keyof typeof elementTypeToResponseKeyMap];
                const generatedText = responseKey ? (data.data as any)[responseKey] : undefined;

                if (generatedText !== undefined) {
                    if (isFrontMatter) {
                        setProjectState(prev => {
                            if (!prev) return prev;
                            return {
                                ...prev,
                                frontMatter: { ...prev.frontMatter, [elementType as AiGeneratableFrontMatterTextKeys]: generatedText },
                                isUnsaved: true,
                            };
                        });
                    } else {
                        setProjectState(prev => {
                            if (!prev) return prev;
                            return {
                                ...prev,
                                backMatter: { ...prev.backMatter, [elementType as AiGeneratableBackMatterTextKeys]: generatedText },
                                isUnsaved: true,
                            };
                        });
                    }
                    successMessage = `${elementType.replace(/([A-Z])/g, ' $1').trim()} generated successfully!`;
                }
            } else if (task === 'structure-multiple') {
                const results: GeneratedBookStructureTexts = data.data;
                const fmChanges: Partial<FrontMatter> = {};
                const bmChanges: Partial<BackMatter> = {};

                for (const key of payload.elementsToGenerate) {
                    const text = results[key as keyof GeneratedBookStructureTexts];
                    if (text !== undefined) {
                        if (Object.prototype.hasOwnProperty.call(initialFrontMatter, key)) {
                            fmChanges[key as AiGeneratableFrontMatterTextKeys] = text;
                        } 
                        else if (Object.prototype.hasOwnProperty.call(initialBackMatter, key)) {
                            bmChanges[key as AiGeneratableBackMatterTextKeys] = text;
                        }
                    }
                }
                setProjectState(prev => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        frontMatter: { ...prev.frontMatter, ...fmChanges },
                        backMatter: { ...prev.backMatter, ...bmChanges },
                        isUnsaved: true,
                    };
                });
                successMessage = 'Selected book structure elements generated successfully!';
            } else if (task === 'image-generation') {
                const base64Image = data.data;
                const imageUrl = `data:image/jpeg;base64,${base64Image}`;
                if (payload.imageType === 'titlePage') {
                    setProjectState(prev => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            frontMatter: { ...prev.frontMatter, titlePageImageBase64: imageUrl },
                            isUnsaved: true,
                        };
                    });
                } else if (payload.imageType === 'backCover') {
                    setProjectState(prev => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            backMatter: { ...prev.backMatter, backCoverImageBase64: imageUrl },
                            isUnsaved: true,
                        };
                    });
                }
                modalControls.setGeneratedImageDataUrl(imageUrl); 
                successMessage = `${payload.imageType === 'titlePage' ? 'Title page' : 'Back cover'} image generated.`;
            }
            toast({ title: "Success", description: successMessage });

            // CRITICAL FIX: Fetch and update progress after book structure generation
            if (projectState?.projectId) {
                try {
                    const resp = await apiRequest(`/api/story-projects/${projectState.projectId}/summary`, 'GET');
                    const progressData = await resp.json();
                    if (progressData && progressData.progress) {

                        // Update project summary cache with fresh progress
                        queryClient.setQueryData(['story-project-summary', projectState.projectId], (oldSummary: any) => {
                            if (oldSummary) {
                                return {
                                    ...oldSummary,
                                    progress: progressData.progress,
                                    updatedAt: new Date().toISOString()
                                };
                            }
                            return progressData;
                        });

                        // Update project-summary localStorage cache
                        cacheManager.set('project-summary', projectState.projectId, {
                            ...progressData,
                        }, 5 * 60 * 1000);

                    }
                } catch (error) {
                    console.warn('Failed to fetch updated progress after book structure generation:', error);
                }
            }
        },
        onError: (error: any, variables) => {
            setError(`AI Error: ${error.message}`);
            toast({ title: "AI Error", description: error.message, variant: "destructive" });
            if (error.rawResponse) modalControls.openDebugResponseModal(error.rawResponse);
        },
        onSettled: (data, error, variables) => {
            const { task, payload } = variables;
            if (task === 'structure-element') {
                const { elementType, isFrontMatter } = payload;
                const loadingKey = isFrontMatter ? 'generateFrontMatterElement' : 'generateBackMatterElement';
                setLoadingState(loadingKey, false);
                setLoadingState('generateTableOfContents', false);
                setLoadingState('generateIndex', false);
            } else if (task === 'structure-multiple') {
                setLoadingState('generateMultipleStructureElements', false);
            } else if (task === 'image-generation') {
                if (payload.imageType === 'titlePage') setLoadingState('generateTitlePageImage', false);
                else if (payload.imageType === 'backCover') setLoadingState('generateBackCoverImage', false);
            }
        }
    });

    const handleAuthorDetailsChange = useCallback(<K extends keyof AuthorDetails>(field: K, value: AuthorDetails[K]) => {
        setProjectState(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                authorDetails: { ...(prev.authorDetails || {} as AuthorDetails), [field]: value },
                isUnsaved: true,
            };
        });
    }, [setProjectState]);

    const handleFrontMatterChange = useCallback(<K extends keyof FrontMatter>(field: K, value: FrontMatter[K]) => {
        setProjectState(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                frontMatter: { ...prev.frontMatter, [field]: value },
                isUnsaved: true,
            };
        });
    }, [setProjectState]);

    const handleBackMatterChange = useCallback(<K extends keyof BackMatter>(field: K, value: BackMatter[K]) => {
        setProjectState(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                backMatter: { ...prev.backMatter, [field]: value },
                isUnsaved: true,
            };
        });
    }, [setProjectState]);

    const handleAddBackMatterListItem = useCallback((listField: 'appendices' | 'glossary' | 'bibliography') => {
        setProjectState(prev => {
            if (!prev) return prev;
            const newList = [...prev.backMatter[listField]];
            const newItemId = `${listField.slice(0, 3)}-${Date.now()}`;
            if (listField === 'appendices') {
                (newList as AppendixItem[]).push({ id: newItemId, title: 'New Appendix', content: '' });
            } else if (listField === 'glossary') {
                (newList as GlossaryTerm[]).push({ id: newItemId, term: 'New Term', definition: '' });
            } else if (listField === 'bibliography') {
                (newList as BibliographyEntry[]).push({ id: newItemId, citation: '' });
            }
            return {
                ...prev,
                backMatter: { ...prev.backMatter, [listField]: newList },
                isUnsaved: true,
            };
        });
    }, [setProjectState]);

    const handleDeleteBackMatterListItem = useCallback((listField: 'appendices' | 'glossary' | 'bibliography', id: string) => {
        setProjectState(prev => {
            if (!prev) return prev;
            const oldList = prev.backMatter[listField] as Array<{ id: string }>;
            const newList = oldList.filter(item => item.id !== id);
            return {
                ...prev,
                backMatter: { ...prev.backMatter, [listField]: newList },
                isUnsaved: true,
            };
        });
    }, [setProjectState]);

    const handleUpdateBackMatterListItem = useCallback((...args: UpdateBackMatterListItemArgsTuple) => {
        setProjectState(prev => {
            if (!prev) return prev;
            const newBackMatter = { ...prev.backMatter };
            const listField = args[0];
            const id = args[1];

            if (listField === 'appendices') {
                const itemField = args[2] as keyof Omit<AppendixItem, 'id'>;
                const value = args[3];
                newBackMatter.appendices = prev.backMatter.appendices.map(item =>
                    item.id === id ? { ...item, [itemField]: value } : item
                );
            } else if (listField === 'glossary') {
                const itemField = args[2] as keyof Omit<GlossaryTerm, 'id'>;
                const value = args[3];
                newBackMatter.glossary = prev.backMatter.glossary.map(item =>
                    item.id === id ? { ...item, [itemField]: value } : item
                );
            } else if (listField === 'bibliography') {
                const itemField = args[2] as keyof Omit<BibliographyEntry, 'id'>;
                const value = args[3];
                newBackMatter.bibliography = prev.backMatter.bibliography.map(item =>
                    item.id === id ? { ...item, [itemField]: value } : item
                );
            }
            return { ...prev, backMatter: newBackMatter, isUnsaved: true };
        });
    }, [setProjectState]);

    const handlePublishingEssentialsChange = useCallback(<K extends keyof PublishingEssentials>(field: K, value: PublishingEssentials[K]) => {
        setProjectState(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                publishingEssentials: { ...prev.publishingEssentials, [field]: value },
                isUnsaved: true,
            };
        });
    }, [setProjectState]);

    const handleGenerateIndividualBookStructureTextElement = useCallback(async (elementType: BookStructureTextElementKey, isFrontMatter: boolean) => {
        if (!apiKey) { setError("Please set API Key."); modalControls.openApiKeyModal(apiKey); return; }
        if (!projectState) { setError("No project loaded."); return; }
        const loadingKey = isFrontMatter ? 'generateFrontMatterElement' : 'generateBackMatterElement';
        setLoadingState(loadingKey, true);
        setLoadingState('generateTableOfContents', elementType === 'tableOfContents');
        setLoadingState('generateIndex', elementType === 'index');
        setError(null);

        const { outlineIdea, bookMetadata, blueprint, authorDetails, chapters } = projectState;
        const worldBuildingData = blueprint.worldBuildingSuggestions;
        const bookContextForAck = { outlineIdea, selectedStoryTitle: worldBuildingData.selectedStoryTitle, genreSubject: bookMetadata.genreSubject };
        const lastThreeChapterSummaries = chapters.slice(-3).map(ch => `Title: ${ch.title}\nSummary: ${ch.summary}\nActive Version Summary: ${getActiveVersion(ch)?.generatedSummary || 'N/A'}`).join('\n\n');
        const chapterTitlesForToC = chapters.map(ch => ({ title: ch.title }));
        const simplifiedChapterContentForIndex = chapters.map(ch => `${ch.title} ${ch.summary} ${getActiveVersion(ch)?.generatedSummary || ''}`).join(' ');

        console.log('[BOOK-STRUCTURE-MANAGEMENT] About to call API with payload:', {
            task: 'structure-element',
            elementType,
            isFrontMatter,
            hasProjectState: !!projectState
        });

        aiMutation.mutate({
            task: 'structure-element',
            payload: {
                elementType,
                isFrontMatter,
                authorDetails,
                outlineIdea,
                bookMetadata,
                worldBuildingData,
                chapters,
                bookContextForAck,
                lastThreeChapterSummaries,
                chapterTitlesForToC,
                simplifiedChapterContentForIndex,
            }
        });
    }, [apiKey, projectState, setLoadingState, setError, modalControls, aiMutation, setProjectState]);
    
    // Type assertion for deprecated handlers to satisfy the interface, they now correctly call the unified handler.
    const handleGenerateFrontMatterElement = useCallback((elementType: Extract<BookStructureTextElementKey, 'dedication' | 'prologue' | 'preface' | 'introduction' | 'acknowledgmentsFront' | 'epigraph' | 'tableOfContents'>) => {
        return handleGenerateIndividualBookStructureTextElement(elementType, true);
    }, [handleGenerateIndividualBookStructureTextElement]);

    const handleGenerateBackMatterElement = useCallback((elementType: Extract<BookStructureTextElementKey, 'epilogue' | 'afterword' | 'authorsNote' | 'acknowledgmentsBack' | 'aboutTheAuthor' | 'readingGroupGuide' | 'teaserNextBook' | 'index'>) => {
        return handleGenerateIndividualBookStructureTextElement(elementType, false);
    }, [handleGenerateIndividualBookStructureTextElement]);

    const handleGenerateMultipleBookStructureElements = useCallback(async (elementsToGenerate: BookStructureTextElementKey[]) => {
        if (!apiKey) { setError("Please set API Key."); modalControls.openApiKeyModal(apiKey); return; }
        if (!projectState) { setError("No project loaded."); return; }
        if (elementsToGenerate.length === 0) { setError("No elements selected for generation."); return; }
        setLoadingState('generateMultipleStructureElements', true); setError(null);

        const { authorDetails, outlineIdea, bookMetadata, blueprint, chapters } = projectState;
        const worldBuildingData = blueprint.worldBuildingSuggestions;
        const chapterTitlesForToC = elementsToGenerate.includes('tableOfContents') ? chapters.map(ch => ({ title: ch.title })) : undefined;

        aiMutation.mutate({
            task: 'structure-multiple',
            payload: {
                authorDetails,
                outlineIdea,
                bookMetadata,
                worldBuildingData,
                elementsToGenerate,
                chapterTitlesForToC,
            }
        });
    }, [apiKey, projectState, setLoadingState, setError, modalControls, aiMutation, setProjectState]);

    const handleGenerateTitlePageImageAI = useCallback(async (prompt: string) => {
        if (!apiKey) { setError("Please set API Key."); modalControls.openApiKeyModal(apiKey); return; }
        if (!projectState) { setError("No project loaded."); return; }
        if (!prompt.trim()) { setError("Please enter a prompt for the title page image."); return; }
        setLoadingState('generateTitlePageImage', true); setError(null);
        aiMutation.mutate({
            task: 'image-generation',
            payload: {
                prompt,
                imageType: 'titlePage',
                bookMetadata: projectState.bookMetadata,
                worldBuildingData: projectState.blueprint.worldBuildingSuggestions,
                outlineIdea: projectState.outlineIdea,
            }
        });
    }, [apiKey, setLoadingState, setError, modalControls, aiMutation, projectState, setProjectState]);

    const handleGenerateBackCoverImageAI = useCallback(async (prompt: string) => {
        if (!apiKey) { setError("Please set API Key."); modalControls.openApiKeyModal(apiKey); return; }
        if (!projectState) { setError("No project loaded."); return; }
        if (!prompt.trim()) { setError("Please enter a prompt for the back cover image."); return; }
        setLoadingState('generateBackCoverImage', true); setError(null);
        aiMutation.mutate({
            task: 'image-generation',
            payload: {
                prompt,
                imageType: 'backCover',
                bookMetadata: projectState.bookMetadata,
                worldBuildingData: projectState.blueprint.worldBuildingSuggestions,
                outlineIdea: projectState.outlineIdea,
            }
        });
    }, [apiKey, setLoadingState, setError, modalControls, aiMutation, projectState, setProjectState]);

    return {
        handleAuthorDetailsChange,
        handleFrontMatterChange,
        handleBackMatterChange,
        handleAddBackMatterListItem,
        handleDeleteBackMatterListItem,
        handleUpdateBackMatterListItem,
        handlePublishingEssentialsChange,
        handleGenerateIndividualBookStructureTextElement,
        handleGenerateMultipleBookStructureElements,
        handleGenerateTitlePageImageAI,
        handleGenerateBackCoverImageAI,
        handleGenerateFrontMatterElement, 
        handleGenerateBackMatterElement,
    };
};